/*
 * Built-in "OpenWarp 编辑器" extension library.
 *
 * These two extensions (admin privileges + local file/folder operations) rely
 * on a native bridge that is only present when the project is run inside an
 * OpenWarp desktop app or a packager-produced Electron app. The bridge is
 * injected into the `Scratch` object by the GUI via the
 * `CREATE_UNSANDBOXED_EXTENSION_API` hook (`Scratch.openwarp`), and it is also
 * exposed on `window.OpenWarpNative` by the Electron preload. When neither is
 * available (e.g. a plain browser) the extensions show a one-time message and
 * return safe default values instead of throwing, so the project never hangs.
 *
 * The extension sources are embedded as data: URLs so they travel inside the
 * project file and can be loaded by the packager without any external host.
 */

const DEFAULT_EXTENSION_ICON = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120">' + '<rect width="120" height="120" rx="16" fill="#ff4c97"/>' + '<text x="60" y="74" font-family="sans-serif" font-size="34" font-weight="bold" fill="#ffffff" text-anchor="middle">Open</text>' + '</svg>');

// The warning shown when the user opens the "OpenWarp 编辑器" library.
const openwarpLibraryWarning = '此扩展库内的扩展与TurboWarp等扩展库的扩展不兼容。用它制作的作品无法在其他编辑器打开。';

// Encode a source string into a data: URL usable by scratch-vm's extension loader.
// Use btoa with a manual UTF-8 encode so this works in any browser renderer
// (Buffer may not be available in the web build).
const utf8Encode = str => encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
const toDataURL = source => `data:text/javascript;base64,${btoa(utf8Encode(source))}`;

// Every extension embeds this same preamble. It resolves the native bridge from
// `Scratch.openwarp` (GUI-injected) or `window.OpenWarpNative` (Electron
// preload), and exposes a `call()` helper that never throws.
const openwarpPreamble = `
var __owWarned = {};
var __owGetBridge = function(Scratch) {
    if (Scratch && Scratch.openwarp) return Scratch.openwarp;
    if (typeof window !== 'undefined' && window.OpenWarpNative) return window.OpenWarpNative;
    return null;
};
var __owCall = function(Scratch, section, name, args) {
    var bridge = __owGetBridge(Scratch);
    var api = bridge && bridge[section];
    if (!api || typeof api[name] !== 'function') {
        if (!__owWarned[name]) {
            __owWarned[name] = true;
            try { alert('此扩展需要 OpenWarp 桌面版或打包器环境才能运行。'); } catch (e) { /* ignore */ }
        }
        return Promise.resolve({ok: false, value: null});
    }
    try {
        return Promise.resolve(api[name].apply(api, args || [])).then(
            function(v) { return {ok: true, value: v}; },
            function() { return {ok: false, value: null}; }
        );
    } catch (e) {
        return Promise.resolve({ok: false, value: null});
    }
};
`;

// ---------------------------------------------------------------------------
// Extension 1: 获取管理员权限
// ---------------------------------------------------------------------------
const openwarpAdminSource = `
(function(Scratch) {
    'use strict';
    if (!Scratch) return;

    class OpenWarpAdmin {
        getInfo() {
            return {
                id: 'openwarpAdmin',
                name: '获取管理员权限',
                color1: '#ff4c97',
                color2: '#e8438a',
                color3: '#d03a7c',
                blocks: [
                    {
                        opcode: 'requestAdmin',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '获取管理员权限'
                    },
                    {
                        opcode: 'hasAdmin',
                        blockType: Scratch.BlockType.BOOLEAN,
                        text: '当前项目是否拥有管理员权限？'
                    }
                ]
            };
        }
        async requestAdmin() {
            const r = await __owCall(Scratch, 'admin', 'request', []);
            return r.ok && !!r.value;
        }
        async hasAdmin() {
            const r = await __owCall(Scratch, 'admin', 'isElevated', []);
            return r.ok && !!r.value;
        }
    }
    Scratch.extensions.register(new OpenWarpAdmin());
})(Scratch);
`;

// ---------------------------------------------------------------------------
// Extension 2: 本地操作
// ---------------------------------------------------------------------------
const openwarpLocalSource = `
(function(Scratch) {
    'use strict';
    if (!Scratch) return;

    class OpenWarpLocal {
        getInfo() {
            return {
                id: 'openwarpLocal',
                name: '本地操作',
                color1: '#ffab19',
                color2: '#ec9a00',
                color3: '#d48a00',
                blocks: [
                    {
                        opcode: 'writeFile',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '在 [PATH] 写入 [DATA]',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/file.txt'},
                            DATA: {type: Scratch.ArgumentType.STRING, defaultValue: 'hello'}
                        }
                    },
                    {
                        opcode: 'readFile',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '获取 [PATH] 以 [MODE] 输出',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/file.txt'},
                            MODE: {
                                type: Scratch.ArgumentType.STRING,
                                menu: 'MODE_MENU',
                                defaultValue: '文本'
                            }
                        }
                    },
                    {
                        opcode: 'createFolder',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '在 [PATH] 里创建 [NAME] 文件夹',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/'},
                            NAME: {type: Scratch.ArgumentType.STRING, defaultValue: '新建文件夹'}
                        }
                    },
                    '---',
                    {
                        opcode: 'moveFile',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '移动 [SRC] 文件到 [DEST]',
                        arguments: {
                            SRC: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/a.txt'},
                            DEST: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/folder'}
                        }
                    },
                    {
                        opcode: 'moveFolder',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '移动 [SRC] 文件夹到 [DEST]',
                        arguments: {
                            SRC: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/folder'},
                            DEST: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/'}
                        }
                    },
                    {
                        opcode: 'copyFile',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '复制 [SRC] 文件到 [DEST]',
                        arguments: {
                            SRC: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/a.txt'},
                            DEST: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/folder'}
                        }
                    },
                    {
                        opcode: 'copyFolder',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '复制 [SRC] 文件夹到 [DEST]',
                        arguments: {
                            SRC: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/folder'},
                            DEST: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/'}
                        }
                    },
                    '---',
                    {
                        opcode: 'searchFiles',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '在 [PATH] 搜索 [NAME] 文件',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/'},
                            NAME: {type: Scratch.ArgumentType.STRING, defaultValue: 'a'}
                        }
                    },
                    {
                        opcode: 'searchFolders',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '在 [PATH] 搜索 [NAME] 文件夹',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/'},
                            NAME: {type: Scratch.ArgumentType.STRING, defaultValue: 'a'}
                        }
                    },
                    '---',
                    {
                        opcode: 'renameFile',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '重命名文件 [PATH] 为 [NEWNAME]',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/a.txt'},
                            NEWNAME: {type: Scratch.ArgumentType.STRING, defaultValue: 'b.txt'}
                        }
                    },
                    {
                        opcode: 'renameFolder',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '重命名文件夹 [PATH] 为 [NEWNAME]',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/folder'},
                            NEWNAME: {type: Scratch.ArgumentType.STRING, defaultValue: 'folder2'}
                        }
                    },
                    '---',
                    {
                        opcode: 'deleteFile',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '删除 [PATH] 的文件',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/a.txt'}
                        }
                    },
                    {
                        opcode: 'deleteFolder',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '删除 [PATH] 的文件夹',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/folder'}
                        }
                    },
                    '---',
                    {
                        opcode: 'fileExists',
                        blockType: Scratch.BlockType.BOOLEAN,
                        text: '[PATH] 的文件是否存在？',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/a.txt'}
                        }
                    },
                    {
                        opcode: 'folderExists',
                        blockType: Scratch.BlockType.BOOLEAN,
                        text: '[PATH] 的文件夹是否存在？',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/folder'}
                        }
                    },
                    '---',
                    {
                        opcode: 'folderCount',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '[PATH] 里的文件夹数量',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/'}
                        }
                    },
                    {
                        opcode: 'fileCount',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '[PATH] 里的文件数量',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/'}
                        }
                    },
                    '---',
                    {
                        opcode: 'fileAttr',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '[PATH] 里的文件 [PROP]',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/a.txt'},
                            PROP: {
                                type: Scratch.ArgumentType.STRING,
                                menu: 'FILE_ATTR_MENU',
                                defaultValue: 'size'
                            }
                        }
                    },
                    {
                        opcode: 'folderAttr',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '[PATH] 里的文件夹 [PROP]',
                        arguments: {
                            PATH: {type: Scratch.ArgumentType.STRING, defaultValue: 'C:/folder'},
                            PROP: {
                                type: Scratch.ArgumentType.STRING,
                                menu: 'FOLDER_ATTR_MENU',
                                defaultValue: 'size'
                            }
                        }
                    }
                ],
                menus: {
                    MODE_MENU: {items: ['文本', 'dataurl']},
                    FILE_ATTR_MENU: {items: ['size', 'type', 'name', 'modified', 'created']},
                    FOLDER_ATTR_MENU: {items: ['size', 'name', 'modified', 'created', 'count']}
                }
            };
        }

        // Command helpers -----------------------------------------------------
        async writeFile(args) { await __owCall(Scratch, 'local', 'writeFile', [args.PATH, args.DATA]); }
        async createFolder(args) { await __owCall(Scratch, 'local', 'createFolder', [args.PATH, args.NAME]); }
        async moveFile(args) { await __owCall(Scratch, 'local', 'moveFile', [args.SRC, args.DEST]); }
        async moveFolder(args) { await __owCall(Scratch, 'local', 'moveFolder', [args.SRC, args.DEST]); }
        async copyFile(args) { await __owCall(Scratch, 'local', 'copyFile', [args.SRC, args.DEST]); }
        async copyFolder(args) { await __owCall(Scratch, 'local', 'copyFolder', [args.SRC, args.DEST]); }
        async renameFile(args) { await __owCall(Scratch, 'local', 'renameFile', [args.PATH, args.NEWNAME]); }
        async renameFolder(args) { await __owCall(Scratch, 'local', 'renameFolder', [args.PATH, args.NEWNAME]); }
        async deleteFile(args) { await __owCall(Scratch, 'local', 'deleteFile', [args.PATH]); }
        async deleteFolder(args) { await __owCall(Scratch, 'local', 'deleteFolder', [args.PATH]); }

        // Reporter helpers ------------------------------------------------------
        async readFile(args) {
            const r = await __owCall(Scratch, 'local', 'readFile', [args.PATH, args.MODE]);
            return r.ok ? r.value : '';
        }
        async searchFiles(args) {
            const r = await __owCall(Scratch, 'local', 'searchFiles', [args.PATH, args.NAME]);
            return JSON.stringify(r.ok ? r.value : []);
        }
        async searchFolders(args) {
            const r = await __owCall(Scratch, 'local', 'searchFolders', [args.PATH, args.NAME]);
            return JSON.stringify(r.ok ? r.value : []);
        }
        async fileExists(args) {
            const r = await __owCall(Scratch, 'local', 'fileExists', [args.PATH]);
            return r.ok && !!r.value;
        }
        async folderExists(args) {
            const r = await __owCall(Scratch, 'local', 'folderExists', [args.PATH]);
            return r.ok && !!r.value;
        }
        async folderCount(args) {
            const r = await __owCall(Scratch, 'local', 'folderCount', [args.PATH]);
            return r.ok ? r.value : 0;
        }
        async fileCount(args) {
            const r = await __owCall(Scratch, 'local', 'fileCount', [args.PATH]);
            return r.ok ? r.value : 0;
        }
        async fileAttr(args) {
            const r = await __owCall(Scratch, 'local', 'getFileAttr', [args.PATH, args.PROP]);
            return r.ok ? r.value : 0;
        }
        async folderAttr(args) {
            const r = await __owCall(Scratch, 'local', 'getFolderAttr', [args.PATH, args.PROP]);
            return r.ok ? r.value : 0;
        }
    }
    Scratch.extensions.register(new OpenWarpLocal());
})(Scratch);
`;

// ---------------------------------------------------------------------------
// Extension 3: 局域网
// ---------------------------------------------------------------------------
const openwarpLanSource = `
(function(Scratch) {
    'use strict';
    if (!Scratch) return;

    class OpenWarpLan {
        getInfo() {
            return {
                id: 'openwarpLan',
                name: '局域网',
                color1: '#2d9cdb',
                color2: '#2487c2',
                color3: '#1d72a8',
                blocks: [
                    {
                        opcode: 'getLocalIP',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '本机IP地址'
                    },
                    {
                        opcode: 'getHostname',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '本机名称'
                    },
                    '---',
                    {
                        opcode: 'broadcast',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '向局域网端口 [PORT] 广播消息 [MSG]',
                        arguments: {
                            PORT: {type: Scratch.ArgumentType.NUMBER, defaultValue: 5000},
                            MSG: {type: Scratch.ArgumentType.STRING, defaultValue: 'hello'}
                        }
                    },
                    {
                        opcode: 'send',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '向IP [IP] 端口 [PORT] 发送消息 [MSG]',
                        arguments: {
                            IP: {type: Scratch.ArgumentType.STRING, defaultValue: '192.168.1.100'},
                            PORT: {type: Scratch.ArgumentType.NUMBER, defaultValue: 5000},
                            MSG: {type: Scratch.ArgumentType.STRING, defaultValue: 'hello'}
                        }
                    },
                    '---',
                    {
                        opcode: 'listen',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '开始监听端口 [PORT]',
                        arguments: {
                            PORT: {type: Scratch.ArgumentType.NUMBER, defaultValue: 5000}
                        }
                    },
                    {
                        opcode: 'stopListen',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '停止监听端口 [PORT]',
                        arguments: {
                            PORT: {type: Scratch.ArgumentType.NUMBER, defaultValue: 5000}
                        }
                    },
                    '---',
                    {
                        opcode: 'hasMessage',
                        blockType: Scratch.BlockType.BOOLEAN,
                        text: '端口 [PORT] 是否收到消息？',
                        arguments: {
                            PORT: {type: Scratch.ArgumentType.NUMBER, defaultValue: 5000}
                        }
                    },
                    {
                        opcode: 'receiveMessage',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '端口 [PORT] 收到的消息',
                        arguments: {
                            PORT: {type: Scratch.ArgumentType.NUMBER, defaultValue: 5000}
                        }
                    },
                    {
                        opcode: 'receiveFrom',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '端口 [PORT] 消息来源IP',
                        arguments: {
                            PORT: {type: Scratch.ArgumentType.NUMBER, defaultValue: 5000}
                        }
                    },
                    {
                        opcode: 'clearReceived',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '清除端口 [PORT] 已读消息',
                        arguments: {
                            PORT: {type: Scratch.ArgumentType.NUMBER, defaultValue: 5000}
                        }
                    }
                ]
            };
        }

        async getLocalIP() {
            const r = await __owCall(Scratch, 'lan', 'getLocalIP', []);
            return r.ok ? r.value : '127.0.0.1';
        }
        async getHostname() {
            const r = await __owCall(Scratch, 'lan', 'getHostname', []);
            return r.ok ? r.value : 'localhost';
        }
        async broadcast(args) { await __owCall(Scratch, 'lan', 'broadcast', [args.PORT, args.MSG]); }
        async send(args) { await __owCall(Scratch, 'lan', 'send', [args.IP, args.PORT, args.MSG]); }
        async listen(args) { await __owCall(Scratch, 'lan', 'listen', [args.PORT]); }
        async stopListen(args) { await __owCall(Scratch, 'lan', 'stopListen', [args.PORT]); }
        async hasMessage(args) {
            const r = await __owCall(Scratch, 'lan', 'hasMessage', [args.PORT]);
            return r.ok && !!r.value;
        }
        async receiveMessage(args) {
            const r = await __owCall(Scratch, 'lan', 'peekMessage', [args.PORT]);
            return r.ok ? r.value : '';
        }
        async receiveFrom(args) {
            const r = await __owCall(Scratch, 'lan', 'peekFrom', [args.PORT]);
            return r.ok ? r.value : '';
        }
        async clearReceived(args) { await __owCall(Scratch, 'lan', 'popReceived', [args.PORT]); }
    }
    Scratch.extensions.register(new OpenWarpLan());
})(Scratch);
`;

// ---------------------------------------------------------------------------
// Extension 4: 窗口控制
// ---------------------------------------------------------------------------
const openwarpWinSource = `
(function(Scratch) {
    'use strict';
    if (!Scratch) return;

    const toBool = v => v === true || v === '是' || v === 'true' || v === 1 || v === '1';

    class OpenWarpWin {
        getInfo() {
            return {
                id: 'openwarpWin',
                name: '窗口控制',
                color1: '#9966ff',
                color2: '#855cd6',
                color3: '#774dcb',
                blocks: [
                    {
                        opcode: 'minimize',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '最小化窗口'
                    },
                    {
                        opcode: 'maximize',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '最大化窗口'
                    },
                    {
                        opcode: 'restore',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '还原窗口'
                    },
                    {
                        opcode: 'setFullScreen',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '全屏 [BOOL]',
                        arguments: {
                            BOOL: {type: Scratch.ArgumentType.STRING, defaultValue: '是'}
                        }
                    },
                    {
                        opcode: 'center',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '窗口居中'
                    },
                    {
                        opcode: 'focus',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '窗口置前并聚焦'
                    },
                    '---',
                    {
                        opcode: 'setTitle',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '设置窗口标题为 [TITLE]',
                        arguments: {
                            TITLE: {type: Scratch.ArgumentType.STRING, defaultValue: 'OpenWarp'}
                        }
                    },
                    {
                        opcode: 'setAlwaysOnTop',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '置顶窗口 [BOOL]',
                        arguments: {
                            BOOL: {type: Scratch.ArgumentType.STRING, defaultValue: '是'}
                        }
                    },
                    {
                        opcode: 'setResizable',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '允许调整窗口大小 [BOOL]',
                        arguments: {
                            BOOL: {type: Scratch.ArgumentType.STRING, defaultValue: '是'}
                        }
                    },
                    {
                        opcode: 'setMaximizable',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '允许最大化 [BOOL]',
                        arguments: {
                            BOOL: {type: Scratch.ArgumentType.STRING, defaultValue: '是'}
                        }
                    },
                    {
                        opcode: 'setSize',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '设置窗口大小 宽 [WIDTH] 高 [HEIGHT]',
                        arguments: {
                            WIDTH: {type: Scratch.ArgumentType.NUMBER, defaultValue: 1280},
                            HEIGHT: {type: Scratch.ArgumentType.NUMBER, defaultValue: 720}
                        }
                    },
                    {
                        opcode: 'setPosition',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '设置窗口位置 X [X] Y [Y]',
                        arguments: {
                            X: {type: Scratch.ArgumentType.NUMBER, defaultValue: 100},
                            Y: {type: Scratch.ArgumentType.NUMBER, defaultValue: 100}
                        }
                    },
                    '---',
                    {
                        opcode: 'windowTitle',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '窗口标题'
                    },
                    {
                        opcode: 'isMinimized',
                        blockType: Scratch.BlockType.BOOLEAN,
                        text: '窗口已最小化？'
                    },
                    {
                        opcode: 'isMaximized',
                        blockType: Scratch.BlockType.BOOLEAN,
                        text: '窗口已最大化？'
                    },
                    {
                        opcode: 'isFullScreen',
                        blockType: Scratch.BlockType.BOOLEAN,
                        text: '窗口全屏中？'
                    },
                    {
                        opcode: 'isAlwaysOnTop',
                        blockType: Scratch.BlockType.BOOLEAN,
                        text: '窗口已置顶？'
                    },
                    {
                        opcode: 'isResizable',
                        blockType: Scratch.BlockType.BOOLEAN,
                        text: '允许调整窗口大小？'
                    },
                    {
                        opcode: 'isMaximizable',
                        blockType: Scratch.BlockType.BOOLEAN,
                        text: '允许最大化？'
                    },
                    {
                        opcode: 'windowWidth',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '窗口宽度'
                    },
                    {
                        opcode: 'windowHeight',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '窗口高度'
                    },
                    {
                        opcode: 'windowX',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '窗口X坐标'
                    },
                    {
                        opcode: 'windowY',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '窗口Y坐标'
                    }
                ],
                menus: {
                    yesno: {
                        acceptReporters: true,
                        items: ['是', '否']
                    }
                }
            };
        }

        async minimize() { await __owCall(Scratch, 'win', 'minimize', []); }
        async maximize() { await __owCall(Scratch, 'win', 'maximize', []); }
        async restore() { await __owCall(Scratch, 'win', 'restore', []); }
        async center() { await __owCall(Scratch, 'win', 'center', []); }
        async focus() { await __owCall(Scratch, 'win', 'focus', []); }
        async setFullScreen(args) {
            await __owCall(Scratch, 'win', 'setFullScreen', [toBool(args.BOOL)]);
        }
        async setTitle(args) {
            await __owCall(Scratch, 'win', 'setTitle', [args.TITLE]);
        }
        async setAlwaysOnTop(args) {
            await __owCall(Scratch, 'win', 'setAlwaysOnTop', [toBool(args.BOOL)]);
        }
        async setResizable(args) {
            await __owCall(Scratch, 'win', 'setResizable', [toBool(args.BOOL)]);
        }
        async setMaximizable(args) {
            await __owCall(Scratch, 'win', 'setMaximizable', [toBool(args.BOOL)]);
        }
        async setSize(args) {
            await __owCall(Scratch, 'win', 'setSize', [args.WIDTH, args.HEIGHT]);
        }
        async setPosition(args) {
            await __owCall(Scratch, 'win', 'setPosition', [args.X, args.Y]);
        }
        async windowTitle() {
            const r = await __owCall(Scratch, 'win', 'getTitle', []);
            return r.ok ? r.value : '';
        }
        async isMinimized() {
            const r = await __owCall(Scratch, 'win', 'isMinimized', []);
            return r.ok && !!r.value;
        }
        async isMaximized() {
            const r = await __owCall(Scratch, 'win', 'isMaximized', []);
            return r.ok && !!r.value;
        }
        async isFullScreen() {
            const r = await __owCall(Scratch, 'win', 'isFullScreen', []);
            return r.ok && !!r.value;
        }
        async isAlwaysOnTop() {
            const r = await __owCall(Scratch, 'win', 'isAlwaysOnTop', []);
            return r.ok && !!r.value;
        }
        async isResizable() {
            const r = await __owCall(Scratch, 'win', 'isResizable', []);
            return r.ok && !!r.value;
        }
        async isMaximizable() {
            const r = await __owCall(Scratch, 'win', 'isMaximizable', []);
            return r.ok && !!r.value;
        }
        async windowWidth() {
            const r = await __owCall(Scratch, 'win', 'getSize', []);
            return r.ok && r.value ? r.value.width : 0;
        }
        async windowHeight() {
            const r = await __owCall(Scratch, 'win', 'getSize', []);
            return r.ok && r.value ? r.value.height : 0;
        }
        async windowX() {
            const r = await __owCall(Scratch, 'win', 'getPosition', []);
            return r.ok && r.value ? r.value.x : 0;
        }
        async windowY() {
            const r = await __owCall(Scratch, 'win', 'getPosition', []);
            return r.ok && r.value ? r.value.y : 0;
        }
    }
    Scratch.extensions.register(new OpenWarpWin());
})(Scratch);
`;

// ---------------------------------------------------------------------------
// Extension 5: cmd调用
// ---------------------------------------------------------------------------
const openwarpCmdSource = `
(function(Scratch) {
    'use strict';
    if (!Scratch) return;

    class OpenWarpCmd {
        getInfo() {
            return {
                id: 'openwarpCmd',
                name: 'cmd调用',
                color1: '#4b4b4b',
                color2: '#3d3d3d',
                color3: '#2f2f2f',
                blocks: [
                    {
                        opcode: 'run',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '执行命令 [CMD]',
                        arguments: {
                            CMD: {type: Scratch.ArgumentType.STRING, defaultValue: 'echo hello'}
                        }
                    },
                    {
                        opcode: 'runAndCapture',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '执行命令 [CMD] 并返回输出',
                        arguments: {
                            CMD: {type: Scratch.ArgumentType.STRING, defaultValue: 'echo hello'}
                        }
                    },
                    '---',
                    {
                        opcode: 'lastOutput',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '最后命令输出'
                    },
                    {
                        opcode: 'lastExitCode',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '最后命令退出码'
                    },
                    {
                        opcode: 'lastSuccess',
                        blockType: Scratch.BlockType.BOOLEAN,
                        text: '最后命令是否成功？'
                    }
                ]
            };
        }

        async run(args) {
            await __owCall(Scratch, 'cmd', 'run', [args.CMD]);
        }
        async runAndCapture(args) {
            const r = await __owCall(Scratch, 'cmd', 'runCapture', [args.CMD]);
            return r.ok && r.value ? (r.value.output != null ? r.value.output : '') : '';
        }
        async lastOutput() {
            const r = await __owCall(Scratch, 'cmd', 'getLast', []);
            return r.ok && r.value ? (r.value.output != null ? r.value.output : '') : '';
        }
        async lastExitCode() {
            const r = await __owCall(Scratch, 'cmd', 'getLast', []);
            return r.ok && r.value && typeof r.value.code === 'number' ? r.value.code : -1;
        }
        async lastSuccess() {
            const r = await __owCall(Scratch, 'cmd', 'getLast', []);
            return r.ok && r.value ? (r.value.code === 0) : false;
        }
    }
    Scratch.extensions.register(new OpenWarpCmd());
})(Scratch);
`;

// ---------------------------------------------------------------------------
// Extension 6: 屏幕截图
// ---------------------------------------------------------------------------
const openwarpScreenSource = `
(function(Scratch) {
    'use strict';
    if (!Scratch) return;

    class OpenWarpScreen {
        getInfo() {
            return {
                id: 'openwarpScreen',
                name: '屏幕截图',
                color1: '#eb5757',
                color2: '#d64545',
                color3: '#bf3b3b',
                blocks: [
                    {
                        opcode: 'capture',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '截取屏幕'
                    },
                    {
                        opcode: 'lastScreenshot',
                        blockType: Scratch.BlockType.REPORTER,
                        text: '最近截图数据'
                    },
                    {
                        opcode: 'captureRegion',
                        blockType: Scratch.BlockType.COMMAND,
                        text: '截屏屏幕 [X] [Y] 至 [X2] [Y2]',
                        arguments: {
                            X: {type: Scratch.ArgumentType.NUMBER, defaultValue: 0},
                            Y: {type: Scratch.ArgumentType.NUMBER, defaultValue: 0},
                            X2: {type: Scratch.ArgumentType.NUMBER, defaultValue: 100},
                            Y2: {type: Scratch.ArgumentType.NUMBER, defaultValue: 100}
                        }
                    }
                ]
            };
        }

        async capture() {
            await __owCall(Scratch, 'screen', 'capture', []);
        }
        async lastScreenshot() {
            const r = await __owCall(Scratch, 'screen', 'getLast', []);
            return r.ok && r.value ? (r.value.dataURL || '') : '';
        }
        async captureRegion(args) {
            await __owCall(Scratch, 'screen', 'captureRegion', [args.X, args.Y, args.X2, args.Y2]);
        }
    }
    Scratch.extensions.register(new OpenWarpScreen());
})(Scratch);
`;

const openwarpLibrary = {
  url: 'builtin:openwarp-editor',
  name: 'OpenWarp编辑器',
  manifestURL: 'builtin:openwarp-editor',
  builtin: true,
  extensions: [{
    name: '获取管理员权限',
    description: '请求并检查当前项目的管理员权限。注意：访问C盘等系统位置通常需要以管理员身份运行 OpenWarp 桌面版或打包器。',
    by: [{
      name: 'OpenWarp'
    }],
    extensionId: 'openwarpAdmin',
    extensionURL: toDataURL(openwarpPreamble + openwarpAdminSource),
    rawURL: DEFAULT_EXTENSION_ICON,
    iconURL: DEFAULT_EXTENSION_ICON,
    tags: ['tw'],
    featured: true,
    incompatibleWithScratch: true,
    requiresAdmin: true
  }, {
    name: '本地操作',
    description: '对本地文件与文件夹进行读取、写入、移动、复制、搜索、重命名、删除等操作。C盘等系统位置的操作需拥有管理员权限。',
    by: [{
      name: 'OpenWarp'
    }],
    extensionId: 'openwarpLocal',
    extensionURL: toDataURL(openwarpPreamble + openwarpLocalSource),
    rawURL: DEFAULT_EXTENSION_ICON,
    iconURL: DEFAULT_EXTENSION_ICON,
    tags: ['tw'],
    featured: true,
    incompatibleWithScratch: true,
    requiresAdmin: true
  }, {
    name: '局域网',
    description: '获取本机IP与本机名称，向局域网内其他设备广播或发送消息，并监听指定端口接收消息。适合多设备互动、设备发现与互联互通场景。',
    by: [{
      name: 'OpenWarp'
    }],
    extensionId: 'openwarpLan',
    extensionURL: toDataURL(openwarpPreamble + openwarpLanSource),
    rawURL: DEFAULT_EXTENSION_ICON,
    iconURL: DEFAULT_EXTENSION_ICON,
    tags: ['tw'],
    featured: true,
    incompatibleWithScratch: true,
    requiresAdmin: false
  }, {
    name: '窗口控制',
    description: '控制OpenWarp窗口本身：最小化、最大化、还原、全屏、置顶与居中，设置窗口标题、是否允许调整大小与最大化、窗口大小与位置，并查询窗口当前状态。适合制作桌面小工具与自动化演示。',
    by: [{
      name: 'OpenWarp'
    }],
    extensionId: 'openwarpWin',
    extensionURL: toDataURL(openwarpPreamble + openwarpWinSource),
    rawURL: DEFAULT_EXTENSION_ICON,
    iconURL: DEFAULT_EXTENSION_ICON,
    tags: ['tw'],
    featured: true,
    incompatibleWithScratch: true,
    requiresAdmin: false
  }, {
    name: 'cmd调用',
    description: '在 OpenWarp 桌面版中执行系统命令（cmd/shell），获取命令输出与退出码。可用于自动化、调用外部程序与脚本。仅 OpenWarp 桌面版或打包器可用。',
    by: [{
      name: 'OpenWarp'
    }],
    extensionId: 'openwarpCmd',
    extensionURL: toDataURL(openwarpPreamble + openwarpCmdSource),
    rawURL: DEFAULT_EXTENSION_ICON,
    iconURL: DEFAULT_EXTENSION_ICON,
    tags: ['tw'],
    featured: true,
    incompatibleWithScratch: true,
    requiresAdmin: false
  }, {
    name: '屏幕截图',
    description: '截取当前屏幕为图片，获取截图数据（data URL）并保存为 PNG 文件。适合制作截图工具、录屏辅助与桌面自动化。仅 OpenWarp 桌面版或打包器可用。',
    by: [{
      name: 'OpenWarp'
    }],
    extensionId: 'openwarpScreen',
    extensionURL: toDataURL(openwarpPreamble + openwarpScreenSource),
    rawURL: DEFAULT_EXTENSION_ICON,
    iconURL: DEFAULT_EXTENSION_ICON,
    tags: ['tw'],
    featured: true,
    incompatibleWithScratch: true,
    requiresAdmin: false
  }]
};

export {
  openwarpLibraryWarning,
  openwarpLibrary
};
